// GIS credential 브로커 (frontend-stability Track B → Session tranche A+B 로 확장)
// 이 모듈이 앱 전체에서 유일하게 google.accounts.id.initialize 를 호출한다. GIS 는 initialize 마다 전역 콜백을
// 교체하므로, 화면(LoginPage)과 갱신 경로가 각자 initialize 하면 서로의 콜백을 빼앗는다(콜백 탈취) —
// 로그인 버튼 응답이 사라지거나, 동시 갱신 중 먼저 시작한 요청이 타임아웃으로 죽는다.
// 계약:
//  - initialize 는 (google, clientId) 당 1회. 콜백은 이 모듈의 deliver 하나뿐.
//  - deliver 우선순위: 진행 중인 재발급 요청 → 없으면 로그인 화면 핸들러(renderButton/One Tap 응답).
//  - requestFreshCredential 은 single-flight — 동시 호출은 같은 promise 를 공유하고 prompt 는 1회만 뜬다.
//  - cancelPendingCredentialRequests(logout·명시적 무효화)는 진행 중 요청을 즉시 실패시키고 provider 프롬프트도
//    닫는다. 취소된 요청자는 이후 도착하는 credential 을 소비하지 못한다.
//  - 취소 시각을 "발급 하한"으로 기록한다. GIS 콜백에는 요청 id 가 없어, 취소 시점에 이미 provider 큐에 들어간
//    콜백이 그 뒤 도착하면 요청 주인이 없어 로그인 화면 핸들러로 흐른다 — 로그아웃 직후 새 LoginPage 가
//    그 credential 로 새 로그인을 시작하는 경로다. 그래서 주인 없는 credential 을 로그인 화면에 넘기기 전,
//    ID token 의 iat(발급 시각, 초)이 하한보다 "확실히 나중"인지만 본다. iat 결손·형식 오류·하한 이하(같은 초 포함)는
//    순서를 증명하지 못하므로 버린다(fail-closed). 이건 인증이 아니다 — 서버 /api/auth-verify 가 여전히 유일한 권위다.
//  - 실패 코드는 기존 그대로(GIS_UNAVAILABLE·GIS_NO_CREDENTIAL·GIS_NOT_DISPLAYED·GIS_TIMEOUT) + 취소
//    (GIS_REQUEST_CANCELLED). 원시 Google 오류·credential 값은 로깅하지 않는다.
// 순수 모듈(google 객체 주입) — node 회귀 테스트가 직접 호출한다(tests/gis-credential-broker.test.mjs).
export const GIS_REQUEST_TIMEOUT_MS = 8000;

let owner = { google: null, clientId: null };
let loginHandler = null;
let interactiveOwner = false;
/** @type {{ promise: Promise<string>, settle: (fn: Function, val: unknown) => void } | null} */
let pending = null;
// 취소(logout) 시각 하한(epoch 초). 0 = 아직 취소 없음 → 하한 없음.
let issuanceFloorSec = 0;

const gisOf = (google) => google?.accounts?.id || null;

// ID token payload 에서 iat(발급 시각, 초)만 읽는다. 값·payload 를 로깅하거나 밖으로 내보내지 않는다.
// 정수가 아니거나 읽을 수 없으면 null.
function issuedAtSec(credential) {
  try {
    const segment = String(credential).split(".")[1];
    if (!segment) return null;
    const json = JSON.parse(
      atob(segment.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return Number.isInteger(json?.iat) ? json.iat : null;
  } catch {
    return null;
  }
}

// 주인 없는 credential 을 로그인 화면에 넘겨도 되는지 — 취소 이후 발급분만 통과(같은 초는 순서 미확정 → 거절).
function issuedAfterCancellation(credential) {
  if (!issuanceFloorSec) return true;
  const iat = issuedAtSec(credential);
  return iat !== null && iat > issuanceFloorSec;
}

// 수신한 credential 을 단 하나의 소비자에게 전달한다. 진행 중 재발급 요청이 우선(그 요청이 프롬프트를 띄웠다),
// 없으면 로그인 화면 핸들러 — 단 취소 이후 발급된 것만. 둘 다 아니면 버린다(주인 없는 credential 을 앱 상태로
// 만들지 않는다).
function deliver(response) {
  const credential = response?.credential || "";
  const target = pending;
  if (target) {
    if (credential) target.settle(target.resolve, credential);
    else target.settle(target.reject, new Error("GIS_NO_CREDENTIAL"));
    return;
  }
  if (credential && loginHandler && (response?.select_by?.startsWith("btn") || issuedAfterCancellation(credential)))
    loginHandler(credential);
}

/**
 * GIS 초기화 보장 — 이미 같은 (google, clientId) 로 초기화돼 있으면 아무것도 하지 않는다.
 * @returns {{ prompt: Function, renderButton: Function }} google.accounts.id
 */
export function ensureGisInitialized({ google, clientId }) {
  const gsi = gisOf(google);
  if (!gsi || !clientId) throw new Error("GIS_UNAVAILABLE");
  if (owner.google === google && owner.clientId === clientId) return gsi;
  gsi.initialize({
    client_id: clientId,
    auto_select: true, // 기존 동의 세션이면 무상호작용 자동 선택(재발급 prompt 전용 — renderButton 은 무관)
    callback: deliver,
  });
  owner = { google, clientId };
  return gsi;
}

/**
 * 로그인 화면(버튼/One Tap) credential 수신자 등록. LoginPage unmount 시 해제한다.
 * @returns {() => void} 해제 함수
 */
export function registerLoginCredentialHandler(handler, { interactive = false } = {}) {
  if (typeof handler !== "function") return () => {};
  if (interactive) {
    // The visible Google button owns recovery; stop the failed One Tap attempt.
    pending?.settle(pending.reject, new Error("GIS_REQUEST_CANCELLED"));
    try { gisOf(owner.google)?.cancel?.(); } catch { /* bounded fallback is the button */ }
  }
  loginHandler = handler;
  interactiveOwner = interactive;
  return () => {
    if (loginHandler === handler) {
      loginHandler = null;
      interactiveOwner = false;
    }
  };
}

/**
 * 무상호작용 재발급 요청(single-flight). 진행 중이면 같은 promise 를 돌려준다.
 * 실패(GIS 부재/미표시/빈 응답/타임아웃/취소)는 reject — 호출부가 고정 코드로 치환한다.
 */
export function requestFreshCredential({
  google,
  clientId,
  timeoutMs = GIS_REQUEST_TIMEOUT_MS,
} = {}) {
  if (interactiveOwner) return Promise.reject(new Error("GIS_INTERACTION_REQUIRED"));
  if (pending) return pending.promise;
  let gsi;
  try {
    gsi = ensureGisInitialized({ google, clientId });
  } catch {
    return Promise.reject(new Error("GIS_UNAVAILABLE"));
  }
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const entry = { promise, resolve, reject, timer: null, settled: false };
  entry.settle = (fn, val) => {
    if (entry.settled) return;
    entry.settled = true;
    clearTimeout(entry.timer);
    if (pending === entry) pending = null;
    fn(val);
  };
  entry.timer = setTimeout(
    () => entry.settle(entry.reject, new Error("GIS_TIMEOUT")),
    timeoutMs,
  );
  pending = entry;
  try {
    gsi.prompt((notification) => {
      // FedCM 전환으로 일부 moment 메서드 부재 가능 — optional chaining, 타임아웃이 최종 안전망
      const blocked =
        notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.();
      if (blocked) entry.settle(entry.reject, new Error("GIS_NOT_DISPLAYED"));
    });
  } catch {
    entry.settle(entry.reject, new Error("GIS_UNAVAILABLE"));
  }
  return promise;
}

/**
 * 진행 중 재발급 요청 취소(logout·세션 무효화). 요청자는 즉시 실패하고, 이후 늦게 도착하는 credential 을
 * 소비하지 못한다. provider 프롬프트도 닫고 자동 선택을 끈다 — 로그아웃 직후 자동 재로그인 방지.
 * 취소 시각을 발급 하한으로 남긴다 — 이 시각 이전에 발급된 credential 은 로그인 화면 핸들러로도 흐르지 않는다.
 */
export function cancelPendingCredentialRequests() {
  // 하한을 먼저 올린다 — settle 이 동기로 부르는 어떤 경로도 옛 하한을 보지 않는다.
  issuanceFloorSec = Math.floor(Date.now() / 1000);
  const target = pending;
  if (target) target.settle(target.reject, new Error("GIS_REQUEST_CANCELLED"));
  const gsi = gisOf(owner.google);
  try {
    gsi?.cancel?.();
    gsi?.disableAutoSelect?.();
  } catch {
    /* provider 상태 정리 실패는 로그아웃을 막지 않는다 */
  }
}
